import { GridDataModel } from 'api/models';
import { NoImageIcon } from 'library/icons';
import { CircularProgress } from 'library/loaders';
import { formatDatetime } from 'modules/shared/utils/date-utils';
import { twMerge } from 'tailwind-merge';
import clsx from 'clsx';
import GridwallSneakPeekModal from 'modules/plugins/features/gridwall-detail/components/GridwallSneakPeekModal/GridwallSneakPeekModal';
import useDialog from 'modules/shared/hooks/useDialog';

interface IOptions {
    showTimestamp: boolean;
    showCameraName: boolean;
    showProjectName: boolean;
    showCustomText: boolean;
    showZoomed: boolean;
}

interface IGridwallDetailProps {
    grid: GridDataModel;
    options: IOptions;
    isLoading?: boolean;
}

const GridwallCard = (props: IGridwallDetailProps) => {
    const { grid, options, isLoading } = props;

    const gridwallSneakPeekDialog = useDialog<{
        grid: GridDataModel;
        options: IOptions;
    }>();

    const marqueeWidth =
        (grid.project?.name?.length ?? 0) + (grid.camera?.name?.length ?? 0) + (grid?.customText?.length ?? 0);

    const hasProject = grid.project?.name && grid.project.name.length > 0;
    const hasCamera = grid.camera?.name && grid.camera.name.length > 0;
    const hasTimeStamp = grid?.latestImage?.dateTime && grid?.latestImage?.dateTime.length > 0;
    const hasMessage = grid?.customText && grid.customText.length > 0;

    const shouldShowMessage = options.showCustomText && hasMessage;
    const shouldShowTimeStamp = options.showTimestamp && hasTimeStamp;
    const shouldShowProjectName = options.showProjectName && hasProject;
    const shouldShowCameraName = options.showCameraName && hasCamera;
    const shouldShowBoth = shouldShowProjectName && shouldShowCameraName;

    // Determine if we should show any bottom overlay content
    const hasBottomContent = shouldShowTimeStamp || shouldShowMessage || shouldShowProjectName || shouldShowCameraName;

    return (
        <>
            <div
                className="relative w-full h-full overflow-hidden min-h-0 min-w-0 bg-grayscale-500 cursor-pointer"
                onClick={() => gridwallSneakPeekDialog.show({ grid, options })}
            >
                {/* Background blur effect - only show when not zoomed or when we have bottom content */}
                {grid?.latestImage?.url && (!options.showZoomed || hasBottomContent) && (
                    <div
                        className="absolute inset-0 bg-cover bg-center bg-no-repeat"
                        style={{
                            backgroundImage: `url(${grid?.latestImage?.url})`,
                            filter: 'blur(100px)',
                            transform: 'scale(1.1)',
                        }}
                    />
                )}

                {/* Dark overlay - adjust opacity based on zoom state */}
                {grid?.latestImage?.url && (
                    <div
                        className={clsx(
                            'absolute inset-0',
                            options.showZoomed && !hasBottomContent ? 'bg-black/0' : 'bg-black/20'
                        )}
                    />
                )}

                {/* Main content area */}
                {isLoading ? (
                    <div className="absolute inset-0 flex items-center justify-center z-10">
                        <CircularProgress size={24} className="text-white" />
                    </div>
                ) : grid?.latestImage?.url ? (
                    <div className="absolute inset-0 z-10 flex items-center justify-center">
                        <img
                            src={grid?.latestImage?.url}
                            alt={'grid'}
                            className={clsx(
                                'transition-all duration-300 ease-in-out',
                                options.showZoomed
                                    ? 'w-full h-full object-cover' // Zoomed: fill entire container
                                    : 'max-w-full max-h-full object-contain' // Not zoomed: maintain aspect ratio
                            )}
                        />
                    </div>
                ) : (
                    <div className="absolute inset-0 z-10 flex items-center justify-center">
                        <NoImageIcon />
                    </div>
                )}

                {/* Bottom overlay - only show when we have content to display */}
                {hasBottomContent && (
                    <div className="absolute bottom-0 left-0 right-0 z-20">
                        <div className="bg-black bg-opacity-75 px-2 sm:px-4 py-1 sm:py-2">
                            <div className="flex items-center justify-between gap-2 sm:gap-4 h-4 text-xs font-semibold">
                                {/* Text content with marquee for long text */}
                                <div
                                    className={twMerge(
                                        'flex-1 min-w-0 overflow-hidden',
                                        marqueeWidth > 30 ? 'marquee' : ''
                                    )}
                                >
                                    <div className="flex items-center gap-1 sm:gap-2 whitespace-nowrap text-xs">
                                        {shouldShowProjectName && (
                                            <span className="text-white ">{grid.project?.name}</span>
                                        )}

                                        {shouldShowBoth && <span className="text-white flex-shrink-0"> | </span>}

                                        {shouldShowCameraName && (
                                            <span className="text-white ">{grid.camera?.name}</span>
                                        )}

                                        {shouldShowMessage && (shouldShowProjectName || shouldShowCameraName) && (
                                            <span className="text-white flex-shrink-0"> | </span>
                                        )}

                                        {shouldShowMessage && <span className="text-white">{grid?.customText}</span>}
                                    </div>
                                </div>

                                {/* Timestamp */}
                                {shouldShowTimeStamp && (
                                    <div className="text-white text-xs whitespace-nowrap flex-shrink-0">
                                        {formatDatetime(grid?.latestImage?.dateTime, 'h:mm a [·] DD MMM, YYYY')}
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                )}
            </div>
            <GridwallSneakPeekModal
                grid={gridwallSneakPeekDialog.data?.grid}
                onClose={gridwallSneakPeekDialog.hide}
                show={gridwallSneakPeekDialog.isOpen}
            />
        </>
    );
};

export default GridwallCard;
